import type { DataTablePaginationState } from '@neo/blocks/cx-data-table'
import type { ApiResponse } from '@shared/types'
import type {
  TaskAssigneeType,
  TaskPriority,
  TaskStatus,
  TaskType,
} from 'v2/pages/conversations/tasks/tasks.config'
import type { TaskAssignmentStatus } from '../../pages/conversations/tasks/tasks.config'
import type { DeleteService, GetService, PatchService, PostService, PutService } from '..'
import type { MonitorStates, RecordingPayload } from '../call'
import type { ChunkState } from '../chunk.internal'
import type { RFileUploadResponse } from './auth'
import type { RTranscription, RWhatsappTemplate } from './settings'

export interface ConversationService {
  getActiveConversations: GetService<RActiveConversation[]>
  updateConversation: PostService<RActiveConversation, Partial<RActiveConversation>, [string]>
  addChunk: PostService<RChunk, Partial<ChunkPayload>, [string]> // Updated method
  getChunks: GetService<
    { chunks: RChunk[]; templates: Record<string, RWhatsappTemplate> },
    [string, number | undefined, number | undefined]
  >
  getRecordingDetails: GetService<{ recording_urls: string[] }, [string]>
  downloadRecordings: PostService<void, DownloadMultipleRecordingsPayload>
  createView: PostService<RViews, Partial<ViewCreatePayload>> // Create View method,
  updateView: PutService<
    RViews,
    {
      uuid: string
      payload: Partial<ViewCreatePayload>
    }
  >
  exportView: PostService<
    RViews,
    {
      uuid: string
      payload: Partial<ExportViewPayload>
    }
  >
  favoriteView: PutService<
    RViews,
    {
      uuid: string
      payload: {
        favourite: boolean
      }
    }
  >
  getViews: GetService<RViews>
  getViewDetails: GetService<RViewDetails, [string]>
  getConversationDetail: GetService<RConversationDetail, [string, Record<string, unknown>]>
  getConversationFilterConfig: GetService<RConversationFilterConfiguration>
  getViewRecords: PostService<RViewRecords, ViewRecordsParams>
  getRecentContacts: GetService<RecentContactsResponse, [Channels, string | undefined]>
  getLiveAgents: GetService<LiveAgent[], [Channels | undefined]>
  getLiveTeams: GetService<Team[], [Channels | undefined]> // Add this new method

  // Task related
  getTasks: PostService<TaskList, Partial<TaskSearchPayload>>
  createTask: PostService<RChunk, TaskPayload>
  updateTask: PatchService<RTask, Partial<TaskPayload>, [string]>
  getTaskActivities: GetService<{ activities: RTaskActivity[] }, [string]>
  createTaskActivity: PostService<RTaskActivity, TaskActivityPayload, [string]>
  updateTaskActivity: PatchService<RTaskActivity, TaskActivityPayload, [string, string]>
  deleteTaskActivity: DeleteService<ApiResponse<void>, DeleteTaskActivityPayload>

  getLiveConversationInViews: PostService<RViewRecords, ViewLiveConversationsParams>
  // call related
  holdCall: PostService<null, HoldCallPayload, [string]>
  toggleRecording: PostService<null, RecordingPayload, [string]>
  mergeParticipant: PostService<null, Partial<TransferPayload>, [boolean]>
  // holdParticipant: PostService<null, TransferPayload>
  toggleCall: PostService<null, Partial<TransferPayload>>
  hangupParticipant: PostService<null, Partial<TransferPayload>>
  initiateCallTransfer: PostService<null, TransferPayload, ['warm' | 'cold' | 'external']>
  cancelCallTransfer: PostService<null, Partial<TransferPayload>, ['warm' | 'cold']>
  // Monitor related methods
  startMonitoring: PostService<void, MonitorPayload>
  startCoaching: PostService<void, CoachPayload>
  startBarging: PostService<void, BargePayload>
  exportRecording: GetService<void, [string, string, string]>
  getTranscription: GetService<RTranscription[], [string]>
}

export interface HoldCallPayload {
  direction: string
  agent_sip_endpoint: string
  hold: boolean
  conversation_number: string
}

export enum ConvChannels {
  CALL = 'call',
  CHAT = 'chat',
  NOTES = 'notes',
  WHATSAPP = 'whatsapp',
  MESSAGE = 'message',
  OMNI = 'omni',
  SLACK = 'slack',
}

export enum ConvGeneratedTypes {
  QNR_GENERATED = 'qnr_generated',
  TRANSFER_GENERATED = 'transfer_generated',
  OUTBOUND_GENERATED = 'outbound_generated',
  TRANSFER_QNR_GENERATED = 'transfer_qnr_generated',
  CHANNEL_SWITCH = 'channel_switch',
  CONTACT_ID_SWITCH = 'contact_id_switch',
}

export enum AdhocChannels {
  TASK = 'task',
  CALLBACK = 'callback',
  NOTES = 'notes',
  PRIVATE_CHAT = 'private chat',
}

export type Channels = ConvChannels | AdhocChannels

export enum ConversationStatus {
  // Also can be found out using !end_at, this is the main way to check if a conversation is open
  OPEN = 'open', // We are not using this now
  IN_WRAP_UP = 'in_wrap_up',
  CLOSED = 'closed',
  TRANSFERRING = 'transferring',
}

export enum AllConversationViewType {
  public = 'public',
  shared = 'shared',
  private = 'private',
}

interface ConversationBase {
  agent_id: string | null // not needed
  channel: Channels
  meta: string | null
  start_at: string | null
  end_at: string | null
  status: ConversationStatus
  contact_uuid: string
}

export interface ChildConversation extends ConversationBase {
  child_direction_inbound: boolean
  generation_type: string
  primary_agent: string // not needed
  sender_id: string
  state: string
  wrap_up_start_at: string | null
}

export interface RActiveConversation extends ConversationBase {
  agent_secondary_conversation_state: MonitorStates | null
  case_id: string | null // not needed
  child_conversations: ChildConversation[]
  closed_conversations: number[] // not needed
  contact_identifier: string
  contact_name: string
  conversation_number: string
  conversation_other_participant_state: MonitorStates | null // Check if this is needed from BE, in UI we use this as fallback to agent_secondary_conversation_state
  crm_contact_id: string | null
  internal_chat_channel: string | null // not needed
  last_activity_at: string // not needed
  last_message: RChunk[] | null // not needed
  linked_conversation: string | null // not needed
  parent_channel: ConvChannels // only needed for resolve API to send disposition, see if we can remove this
  parent_contact_uuid: string // not needed
  parent_end_at: string | null // not needed
  parent_start_at: string
  phlo_closure_flag: boolean // not needed
  primary_agent_name: string // Needed only for transfer_merge_success event
  secondary_participants: string[]
  secondary_participants_details: {
    agent_name: string
    agent_uuid: string
  }[]
  source_direction_inbound: boolean
  templates_data: string // not needed
  unviewed_count: number // not needed
}

export interface RNewConversation {
  assigned_to: string
  channel: ConvChannels | AdhocChannels
  chunks: unknown
  contact_name: string
  contact_uuid: string
  conversation_number: string
  crm_contact_id: string
  customer_id: string
  customerId?: string
  end_at: string
  invite_type: 'TRANSFER' | 'INITIATE'
  parent_direction: string
  start_at: string
  sender_id: string
  is_agent_created_task?: boolean
}

export interface RMonitoringData {
  supervisor_message: string
  agent_message: string
  supervisor_name: string
  agent_name: string
}

export interface RChunk {
  attributes?: unknown[] | null
  channel: Channels
  channel_meta: {
    error: string | null
    error_data: {
      agentAction: boolean
      category: string
      comments: string
      description: string
      errorType: 'Whole conversation' | 'Specific message'
      message: string
    } | null
    replied_message: RChunk | null
    replied_to: string | null
    status: ChunkState | null
    timestamp: string | null
    transfer_type: string | null
  }
  contact_uuid: string | null
  conversation_number?: string
  customer_contact: string | null
  description: string | null
  disposition: string | null
  disposition_id: string | null
  from: string
  from_user_name: string | null
  from_user?: string | null
  is_first_chunk_for_conv: boolean
  is_inbound_chunk: boolean
  message_id: string | null
  reply_to: string | null
  retry_chunk_uuid: string | null
  sender_id: string | null
  sub_disposition: string | null
  sub_disposition_id: string | null
  timestamp: string
  to: string
  to_user_name: string | null
  type: string
  uuid: string
  id: string
}

export interface RMessageNotification {
  message_id: string
  conversation_number: string
  contact_uuid: string
  status: string
  error: string
  error_data: RChunk['channel_meta']['error_data']
}

export interface ChunkPayload {
  attributes?: unknown[]
  type: string
  description: string
  disposition: string
  sub_disposition: string
  channel: string
  from: string
  to?: string
  sender_id?: string
}

export interface TaskScheduleData {
  schedule_start_time_utc: string
  schedule_start_time_local: string
  task_schedule_time_type: string
  timezone: string
}

export interface RTask {
  attachments: RFileUploadResponse[] | null
  channel: ConvChannels | null
  created_at: string
  destination_id: string
  destination_identifier_name: string
  destination_type: TaskAssigneeType
  conversation?: RNewConversation | null
  conversation_number: string
  conversation_channel: ConvChannels | null
  conversation_closed: boolean
  due_date: string
  from_identifier: string
  organization: number
  priority: TaskPriority
  routing_status: string | null
  schedule_data: TaskScheduleData | null
  source_id: string
  source_identifier_name: string
  source_type: TaskAssigneeType
  status: TaskStatus
  task_data: {
    description: string
    title: string
  }
  type: TaskType
  uuid: string
}

export interface DownloadRecordingsPayload {
  fields: string[]
  filters: Record<string, unknown>
  view_id: string
  page: number
  search_query: string
  client_timezone: string
}

export interface TaskScheduledData {
  schedule_start_time_local: string
  task_schedule_time_type: string
  schedule_start_time_utc?: string
  timezone: string
}

export interface TaskList {
  tasks: RTask[]
  total_count: number
}

export interface TaskSearchPayload {
  filters: {
    conversation_number?: string | ''
    search_term?: string | ''
    task_type?: TaskType[] | null
    status?: TaskStatus[] | null
    priority?: TaskPriority[] | null
    owner_uuids?: string[] | null
    assignment_status?: TaskAssignmentStatus[] | null
  }
  page: number
  limit: number
}

export interface TaskPayload {
  existing_conversation_number?: string
  type: TaskType
  channel: ConvChannels | null
  schedule_data?: TaskScheduledData | null
  is_scheduled_task?: boolean
  is_reassigned?: boolean
  task_data: {
    title: string
    description: string
  }
  source_type: TaskAssigneeType
  destination_type: TaskAssigneeType
  destination_id?: string
  status: TaskStatus
  priority: TaskPriority
  due_date: string
  attachments: RFileUploadResponse[] | null
}

export interface TaskForm {
  uuid?: string
  task_type: TaskType
  channel: ConvChannels
  schedule?: Date
  timezone?: string
  title?: string
  description: string
  assignee_type: TaskAssigneeType
  assignee?: string
  priority: TaskPriority
  status: TaskStatus
  dueDate: Date | undefined
  attachments: RFileUploadResponse[] | null
}

export interface TaskActivityPayload {
  comment: string
  attachments: RFileUploadResponse[] | null
}

export interface DeleteTaskActivityPayload {
  taskUuid: string
  activityUuid: string
}

export interface RTaskActivity {
  activity_id: string
  activity_time: string
  attachments: RFileUploadResponse[]
  author_id: string
  author_name: string
  author_type: string
  comment: string | null
  meta: {
    action: string
    old_value: string | TaskScheduledData
    new_value: string | TaskScheduledData
    activity_type: string
    destination_id: string
    destination_name: string
    destination_type: string
    routing_status: string
    schedule_time: string
    schedule_timezone: string
  }
}

export interface ViewCreatePayload {
  name: string
  view_type: 'public' | 'shared' | 'private'
  fields: string[]
  filters: FiltersType | Record<string, never>
  shared: Record<'aom' | 'team', string[]> | object
}

export interface ExportViewPayload {
  all_conversations: boolean
  client_timezone: string
  export_type: 'conv_summary' | 'conv_details' | 'conv_summary_details'
  file_type: 'csv' | 'xlsx' | 'pdf' | 'txt'
  selected_conversations: string[]
  unselected_conversations: string[]
  view_search_config: {
    fields: string[]
    filters: FiltersType | Record<string, never>
  }
}

export interface DownloadMultipleRecordingsPayload {
  all_conversations: boolean
  selected_conversations: string[]
  unselected_conversations: string[]
  view_search_config: {
    fields: string[]
    filters: FiltersType | Record<string, never>
    search_query: string
  }
}

export interface RView {
  name: string
  owner_uuid: string
  uuid: string
}

export interface RViewDetails {
  fields: string[]
  name: string
  owner_uuid: string
  view_type: AllConversationViewType
  shared: {
    name: string
    type: 'aom' | 'team'
    uuid: string
  }[]
  filters: FiltersType | Record<string, never>
}

export interface RViews {
  favourite: RView[]
  private: RView[]
  public: RView[]
  shared: RView[]
}

export interface RConversationDetail {
  chunks: RChunk[]
  templates: Record<string, RWhatsappTemplate>
  conversation_details: {
    customer_name: string
    crm_contact_id: string
    conversation_closed: boolean
    parent_channel: ConvChannels
  }
}

export type RConversationFilterConfiguration = {
  api_id: string
  data: ConversationFilterFieldData[]
  errors: null | string
  message: string
}

export type ConversationFilterFieldData = {
  active_conversation_field?: boolean
  attribute_name: string
  display: boolean
  editable: boolean
  filter?: ConversationFilter
  label: string
}

export type ConversationFilter = {
  accepted_values: Record<string, ConversationFilterAcceptedValue[]>
  field_type: string
  filter_attribute: string
  operations: ConversationFilterOperation[]
}

export type ConversationFilterAcceptedValue = {
  label: string
  value: string
  sub_dispositions?: SubDisposition[]
}

export type SubDisposition = {
  label: string
  value: string
}

export type ConversationFilterOperation =
  | 'is'
  | 'is_not'
  | 'contains'
  | 'does_not_contain'
  | 'empty'
  | 'not_empty'
  | 'after'
  | 'on_and_after'
  | 'before'
  | 'on_and_before'
  | 'between'
  | 'greater_than'
  | 'greater_than_or_equal_to'
  | 'less_than'
  | 'less_than_or_equal_to'
  | 'contains any of'

export interface RRecord {
  agent_details?: Array<{
    agent_name: string
    teams?: Array<{
      team_name: string
    }>
  }>
  case_id: string | null
  contact_identifier: string
  conv_child_sender_ids: string[]
  conv_chunk_message_types: string[]
  conv_created_date: string
  conv_created_time: string
  conv_number: string
  conv_state: string | null
  customer_name: string | null
  direction_inbound: boolean
  disposition: string | null
  hangup_source: string | null
  parent_channel: ConvChannels
  sub_disposition: string | null
  wrap_up_note: string | null
  csat_feedback: string | null
  conv_total_duration: string | null
  conv_response_time: string | null
  conv_wrapup_duration: string | null
}
export interface RViewRecords {
  metadata: null
  page: number
  page_size: number
  records: RRecord[]
  total_count: number
}

export interface ViewRecordsParams {
  view_id: string
  page: number
  fields: string[]
  filters: FiltersType | Record<string, never>
  search_query: string
  client_timezone: string
}

export interface ViewLiveConversationsParams {
  page: number
  fields: string[]
  filters: FiltersType | Record<string, never>
  client_timezone: string
}

export interface IFilterChild {
  op_type: string
  type: string
  value: string | string[] | Record<string, unknown>[] | unknown
}

export interface IFilterGroup {
  filter_children: IFilterChild[]
  filter_conditions: string[]
  group_child: boolean
}

export type FiltersType = {
  filter_groups: IFilterGroup[]
  group_conditions: ('AND' | 'OR')[]
}

export interface IConversationsFilters {
  filters: FiltersType | Record<string, never>
}

export type ViewRecordsWithPagination = Omit<ViewRecordsParams, 'page'> & {
  pagination: DataTablePaginationState
}

export type ConversationData = RActiveConversation[] | RNewConversation[] | RViewRecords['records']

export interface ConversationUITempData {
  conversationNumber: string
  chunks: RChunk[]
  contactName: string
  contactIdentifier: string
  contactUUID: string
  startAt: string
  wrapUpStartAt: string | null
}

// Add these new types
export interface RecentContact {
  contact_direction: 'inbound' | 'outbound'
  contact_identifier: string
  direction_inbound: boolean
  last_activity_at: string
  name: string
  contact_id: string
}

export interface RecentContactsResponse {
  crm_contacts: RecentContact[]
  crm_contacts_count: number
  non_crm_contacts: RecentContact[]
  non_crm_contacts_count: number
  page: number
  page_size: number
  total_count: number
}

export interface InitiateWhatsAppConversationPayload {
  from: string
  to: string
  template_attributes: {
    components: Array<{
      type: string
      parameters: Array<{
        type: string
        text: string
      }>
    }>
    language: {
      policy: string
      code: string
    }
    name: string
    namespace: string
    template_uuid: string
  }
}
export interface InitiateSmsConversationPayload {
  from: string
  to: string
  description: string
  attributes?: RFileUploadResponse[]
}

// Add this new interface
export interface LiveAgent {
  account_first_name: string
  account_last_name: string
  email: string
  id: string
  roles: string[]
  sip_endpoint: string
  status: string
  status_availability: boolean
  status_color: string
  teams: Array<{
    aom_id: string
    created_at: string
    id: string
    role_name: string
    team_name: string
    team_uuid: string
    updated_at_epoch: number
  }>
}

// Add this new interface
export interface Team {
  agent_count: number
  name: string
  uuid: string
}

// Add this new interface
export interface TransferPayload {
  contact_uuid: string
  transfer_type: 'agent' | 'team' | 'external number'
  identifier: string
  agent_sip: string
  push_contact_details_async_flag?: boolean
  direction_inbound: boolean
  channel?: ConvChannels
  member?: string
}

// Add monitor-related interfaces
export interface MonitorPayload {
  state: MonitorStates
  conversation_id: string
}

export interface CoachPayload {
  conversation_id: string
  agent_requested_coach: boolean
}

export interface BargePayload {
  conversation_id: string
}
